import React from 'react';
import TableCell from '@material-ui/core/TableCell';
import { HeaderRowConfig } from '../../../components/CustomTable/CustomTable';
import {
  OpportunityPropertyStatus,
  OpportunityReportSummary,
} from '../../../redux/types/reports';
import { Order } from '../../../utils/table';
import {
  ExpansionOpportunityLevel,
  OpportunityCommunityTableRow,
  OpportunityPropertyTableRow,
  PropertyActionVariant,
  OpportunityTabKey,
} from '../types';

export type CsvColumn<T extends object> = {
  label: string;
  value: (row: T) => string | number | null | undefined;
};

export const opportunityReportConfig = {
  defaultTab: 'communities' as OpportunityTabKey,
  rowsPerPage: 25,
  community: {
    order: 'asc' as Order,
    orderBy: 'communityName' as keyof OpportunityCommunityTableRow,
  },
  property: {
    order: 'asc' as Order,
    orderBy: 'address' as keyof OpportunityPropertyTableRow,
  },
};

export const defaultOpportunitySummary: OpportunityReportSummary = {
  builderName: '',
  states: [],
  feedCommunities: 0,
  activeCommunities: 0,
  communityOpportunities: 0,
  feedSpecHomes: 0,
  activeProperties: 0,
  propertyOpportunities: 0,
  needsReviewCommunities: 0,
  needsReviewProperties: 0,
};

export const expansionOpportunityLabels: Record<
ExpansionOpportunityLevel,
string
> = {
  none: 'No expansion',
  small: 'Small expansion',
  big: 'Big expansion',
};

export const bigExpansionCellStyle = {
  backgroundColor: '#E4F3E7',
  color: '#2E6A3B',
  fontWeight: 600,
};

const propertyActionCellStyles: Record<PropertyActionVariant, React.CSSProperties> = {
  default: {
    backgroundColor: 'transparent',
  },
  create: {
    backgroundColor: '#E4F3E7',
    color: '#2E6A3B',
    fontWeight: 600,
  },
  update: {
    backgroundColor: '#FFF4CC',
    color: '#8A6D1E',
    fontWeight: 600,
  },
  blocked: {
    backgroundColor: '#FCE8E6',
    color: '#B42318',
    fontWeight: 600,
  },
};

const blockedPropertyLabels: Record<string, string> = {
  DUPLICATE: 'Duplicate',
  ID_CONFLICT: 'ID Conflict',
};

export const getExpansionOpportunity = (specHomesCount: number) => {
  if (specHomesCount === 0) {
    return {
      label: expansionOpportunityLabels.none,
      level: 'none' as ExpansionOpportunityLevel,
      rank: 0,
      isBigExpansion: false,
    };
  }

  if (specHomesCount <= 5) {
    return {
      label: expansionOpportunityLabels.small,
      level: 'small' as ExpansionOpportunityLevel,
      rank: 1,
      isBigExpansion: false,
    };
  }

  return {
    label: expansionOpportunityLabels.big,
    level: 'big' as ExpansionOpportunityLevel,
    rank: 2,
    isBigExpansion: true,
  };
};

export const getPropertyAction = (
  status: OpportunityPropertyStatus,
) => {
  switch (status) {
    case 'MISSING_PROPERTY':
      return {
        label: 'Create',
        variant: 'create' as PropertyActionVariant,
        rank: 0,
      };
    case 'UPDATE_PROPERTY':
      return {
        label: 'Update',
        variant: 'update' as PropertyActionVariant,
        rank: 1,
      };
    case 'COVERED':
      return {
        label: 'No Changes',
        variant: 'default' as PropertyActionVariant,
        rank: 1,
      };
    default:
      return {
        label: blockedPropertyLabels[status] || 'Blocked',
        variant: 'blocked' as PropertyActionVariant,
        rank: 2,
      };
  }
};

export const getOpportunitySummaryItems = (
  summary: OpportunityReportSummary,
) => [
  {
    label: 'Feed Communities',
    value: summary.feedCommunities,
    caption: 'Communities found in the XML feed',
  },
  {
    label: 'Active Communities',
    value: summary.activeCommunities,
    caption: `${summary.communityOpportunities} new, ${summary.needsReviewCommunities} need review`,
  },
  {
    label: 'Spec Homes',
    value: summary.feedSpecHomes,
    caption: 'Spec homes found in the XML feed',
  },
  {
    label: 'Active Properties',
    value: summary.activeProperties,
    caption: `${summary.propertyOpportunities} new, ${summary.needsReviewProperties} need review`,
  },
];

export const communityHeaderRows: HeaderRowConfig<
OpportunityCommunityTableRow
>[] = [
  {
    key: 'state',
    align: 'center',
    disablePadding: false,
    label: 'State',
  },
  {
    key: 'city',
    align: 'center',
    disablePadding: false,
    label: 'City',
  },
  {
    key: 'zip',
    align: 'center',
    disablePadding: false,
    label: 'ZIP',
  },
  {
    key: 'communityName',
    align: 'center',
    disablePadding: false,
    label: 'Community',
  },
  {
    key: 'externalCommunityId',
    align: 'center',
    disablePadding: false,
    label: 'External Community ID',
    prepareData: ({ externalCommunityId }) => externalCommunityId || 'N/A',
  },
  {
    key: 'activePropertyCount',
    align: 'center',
    disablePadding: false,
    label: 'Active Properties',
  },
  {
    key: 'feedSpecHomesCount',
    align: 'center',
    disablePadding: false,
    label: 'Spec Homes',
  },
  {
    key: 'rank',
    align: 'center',
    disablePadding: false,
    label: 'Expansion Opportunity',
    render: (row) => React.createElement(
      TableCell,
      {
        align: 'center',
        key: 'rank',
        style: row.isBigExpansion ? bigExpansionCellStyle : undefined,
      },
      row.label,
    ),
  },
];

export const propertyHeaderRows: HeaderRowConfig<
OpportunityPropertyTableRow
>[] = [
  {
    key: 'state',
    align: 'center',
    disablePadding: false,
    label: 'State',
  },
  {
    key: 'city',
    align: 'center',
    disablePadding: false,
    label: 'City',
  },
  {
    key: 'zip',
    align: 'center',
    disablePadding: false,
    label: 'ZIP',
  },
  {
    key: 'communityName',
    align: 'center',
    disablePadding: false,
    label: 'Community',
  },
  {
    key: 'address',
    align: 'center',
    disablePadding: false,
    label: 'Address',
  },
  {
    key: 'externalIdentifier',
    align: 'center',
    disablePadding: false,
    label: 'External Identifier',
    prepareData: ({ externalIdentifier }) => externalIdentifier || 'N/A',
  },
  {
    key: 'externalCommunityId',
    align: 'center',
    disablePadding: false,
    label: 'External Community ID',
    prepareData: ({ externalCommunityId }) => externalCommunityId || 'N/A',
  },
  {
    key: 'rank',
    align: 'center',
    disablePadding: false,
    label: 'Action',
    render: (row) => React.createElement(
      TableCell,
      {
        align: 'center',
        key: 'rank',
        style: propertyActionCellStyles[row.variant],
      },
      row.label,
    ),
  },
];

export const communitySearchValueGetters = [
  (row: OpportunityCommunityTableRow) => row.communityName,
  (row: OpportunityCommunityTableRow) => row.city,
  (row: OpportunityCommunityTableRow) => row.state,
  (row: OpportunityCommunityTableRow) => row.zip,
  (row: OpportunityCommunityTableRow) => row.externalCommunityId,
  (row: OpportunityCommunityTableRow) => row.activePropertyCount,
  (row: OpportunityCommunityTableRow) => row.feedSpecHomesCount,
  (row: OpportunityCommunityTableRow) => row.label,
];

export const propertySearchValueGetters = [
  (row: OpportunityPropertyTableRow) => row.communityName,
  (row: OpportunityPropertyTableRow) => row.address,
  (row: OpportunityPropertyTableRow) => row.city,
  (row: OpportunityPropertyTableRow) => row.state,
  (row: OpportunityPropertyTableRow) => row.zip,
  (row: OpportunityPropertyTableRow) => row.externalIdentifier,
  (row: OpportunityPropertyTableRow) => row.externalCommunityId,
  (row: OpportunityPropertyTableRow) => row.label,
];

export const communityExportColumns: CsvColumn<
OpportunityCommunityTableRow
>[] = [
  { label: 'State', value: (row) => row.state },
  { label: 'City', value: (row) => row.city },
  { label: 'ZIP', value: (row) => row.zip },
  { label: 'Community', value: (row) => row.communityName },
  { label: 'External Community ID', value: (row) => row.externalCommunityId },
  { label: 'Active Properties', value: (row) => row.activePropertyCount },
  { label: 'Spec Homes', value: (row) => row.feedSpecHomesCount },
  {
    label: 'Expansion Opportunity',
    value: (row) => row.label,
  },
];

export const propertyExportColumns: CsvColumn<OpportunityPropertyTableRow>[] = [
  { label: 'State', value: (row) => row.state },
  { label: 'City', value: (row) => row.city },
  { label: 'ZIP', value: (row) => row.zip },
  { label: 'Community', value: (row) => row.communityName },
  { label: 'Address', value: (row) => row.address },
  { label: 'External Identifier', value: (row) => row.externalIdentifier },
  { label: 'External Community ID', value: (row) => row.externalCommunityId },
  { label: 'Action', value: (row) => row.label },
];

export const opportunityReportFileNames = {
  communities: 'opportunity-communities-report.csv',
  properties: 'opportunity-properties-report.csv',
};
